import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type { User, UserUpdatePayload } from '@/types/user'

interface RoleCheckResponse {
  role_changed: boolean
  current_role: string
  token_role: string
}

export const useUserStore = defineStore('user', () => {
  const currentUser = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const fetchCurrentUser = async () => {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await api.get(API_ROUTES.USER_GET_CURRENT)
      if (!response.data || !response.data.uuid) {
        // Throw error so callers can handle auth failures
        throw new Error('Invalid user data received')
      }
      currentUser.value = response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch user data'
      console.error('Error fetching user:', err)
      // Throw error so callers can handle auth failures
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const clearCurrentUser = () => {
    currentUser.value = null
  }

  const setCurrentUser = (user: User) => {
    currentUser.value = user
  }

  const updateProfile = async (payload: UserUpdatePayload) => {
    isLoading.value = true
    error.value = null

    try {
      const cleanPayload = {
        ...payload,
        gender: payload.gender?.toString(),
        current_job_role: payload.current_job_role?.toString(),
        country_code_iso_2: payload.country_code_iso_2?.toString()
      };

      const response = await api.put(API_ROUTES.USER_UPDATE, cleanPayload);
      currentUser.value = response.data;
      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update profile'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const updateAvatar = async (formData: FormData) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.put(API_ROUTES.USER_UPLOAD_AVATAR, formData)
      if (currentUser.value) {
        currentUser.value.avatar_image = response.data.avatar_image
      }
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update avatar'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    const { useAuthStore } = await import('./auth')
    const authStore = useAuthStore()
    
    // Use auth store's logout method (await it to ensure cleanup completes)
    await authStore.logout()
    
    // Clear current user
    currentUser.value = null
    
    // DO NOT redirect here - let callers handle redirect after logout completes
    // This prevents race conditions where cleanup might not finish before navigation
  }

  const checkUserRole = async (): Promise<RoleCheckResponse> => {
    try {
      const { decodeJWT } = await import('@/utils/jwtDecoder')
      const token = localStorage.getItem('token')

      if (token) {
        try {
          const decoded = decodeJWT(token)
          if (decoded) {
            const tokenRole = (
              decoded.role ||
              decoded.user_role ||
              decoded.userRole ||
              ''
            )
              .toString()
              .toUpperCase()

            if (tokenRole) {
              return {
                role_changed: false,
                current_role: tokenRole,
                token_role: tokenRole
              }
            }
          }
        } catch (err) {
          console.warn('[UserStore] Failed to decode token:', err)
        }
      }

      const { getRoleFromCookie } = await import('@/utils/roleHelpers')
      const cookieRole = getRoleFromCookie()
      if (cookieRole) {
        return {
          role_changed: false,
          current_role: cookieRole,
          token_role: cookieRole
        }
      }

      return {
        role_changed: false,
        current_role: '',
        token_role: ''
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to check user role from token'
      console.error('Error checking user role from token:', err)
      return {
        role_changed: false,
        current_role: '',
        token_role: ''
      }
    }
  }

  return {
    currentUser,
    isLoading,
    error,
    fetchCurrentUser,
    updateProfile,
    updateAvatar,
    clearCurrentUser,
    setCurrentUser,
    logout,
    checkUserRole
  }
}) 